import React, { useEffect, useState } from 'react';
import type { CauseAnalysisMethod, RootCauseAnalysis } from '../../types/issue';
import { ensureIshikawa } from '../../utils/causeDraft';
import type { IssueEvidenceRecord } from '../../services/issueEvidenceService';
import type { CauseCheckView } from '../../utils/causeWork';
import { CauseAnalysisView, type CauseObservationContext } from './CauseAnalysisView';

const METHOD_LABEL = {
  five_whys: '5 Porquês',
  ishikawa: 'Ishikawa 6M',
  simple: 'Análise direta',
};

function blank(value: RootCauseAnalysis | null): RootCauseAnalysis {
  return {
    problem_statement: value?.problem_statement ?? '',
    root_cause_description: value?.root_cause_description ?? '',
    root_cause_category: value?.root_cause_category ?? 'method',
    five_whys: value?.five_whys?.length
      ? value.five_whys
      : [1, 2, 3, 4, 5].map((level) => ({ level, question: `Por quê ${level}`, answer: '' })),
    ishikawa: ensureIshikawa(value?.ishikawa),
    suggested_hypotheses: value?.suggested_hypotheses,
    cause_confirmed: value?.cause_confirmed,
    cause_evidence: value?.cause_evidence,
    analysis_method: value?.analysis_method,
  };
}

interface CauseWorkspaceProps {
  value: RootCauseAnalysis | null;
  focus: 'investigation' | 'cause';
  readOnly?: boolean;
  saving?: boolean;
  onSave: (analysis: RootCauseAnalysis) => void | Promise<void>;
  onContinueToCause?: () => void;
  onReviewObservation?: () => void;
  observation?: CauseObservationContext;
  evidence?: IssueEvidenceRecord[];
  onAddEvidence?: (analysis: RootCauseAnalysis, hypothesisId: string, file: File, caption: string) => Promise<void>;
  onDeleteEvidence?: (record: IssueEvidenceRecord) => Promise<void>;
  causeCheck?: CauseCheckView | null;
  onSaveCauseCheck?: (check: CauseCheckView) => Promise<void>;
  focusToken?: string | null;
  onDefinePlan?: () => void;
}

export const CauseWorkspace: React.FC<CauseWorkspaceProps> = ({
  value,
  focus,
  readOnly = false,
  saving = false,
  onSave,
  onContinueToCause,
  onReviewObservation,
  observation,
  evidence,
  onAddEvidence,
  onDeleteEvidence,
  causeCheck,
  onSaveCauseCheck,
  focusToken,
  onDefinePlan,
}) => {
  const [data, setData] = useState<RootCauseAnalysis>(() => blank(value));

  useEffect(() => {
    setData(blank(value));
  }, [value]);

  if (focus === 'cause') {
    return (
      <CauseAnalysisView
        value={value}
        readOnly={readOnly}
        saving={saving}
        onSave={onSave}
        onReviewObservation={onReviewObservation}
        observation={observation}
        evidence={evidence}
        onAddEvidence={onAddEvidence}
        onDeleteEvidence={onDeleteEvidence}
        causeCheck={causeCheck}
        onSaveCauseCheck={onSaveCauseCheck}
        focusToken={focusToken}
        onDefinePlan={onDefinePlan}
      />
    );
  }

  const save = (next: RootCauseAnalysis) => {
    setData(next);
    void onSave(next);
  };
  const phenomenon = data.problem_statement.trim();

  return (
    <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,860px)_240px]">
      <div className="min-w-0 max-w-[860px] space-y-2">
        <p className="text-[11px] font-semibold tracking-wide text-slate-400">Observar → Fenômeno → Análise</p>
        <section className="rounded border border-slate-200">
          <div className="border-b border-slate-100 px-2.5 py-1.5">
            <p className="text-[12px] font-semibold text-slate-800">Fenômeno observado</p>
          </div>
          <div className="space-y-2 px-2.5 py-2">
            {readOnly ? (
              <p className="text-[13px] text-slate-800">{phenomenon || 'Ainda não descrito.'}</p>
            ) : (
              <textarea
                value={data.problem_statement}
                onChange={(event) => setData((current) => ({ ...current, problem_statement: event.target.value }))}
                rows={4}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-[13px]"
                placeholder="O que aconteceu, onde e como aparece."
              />
            )}
            {!readOnly ? (
              <button type="button" disabled={saving} onClick={() => save(data)} className="rounded border border-slate-300 px-3 py-1.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50">Salvar observação</button>
            ) : null}
            {onContinueToCause ? (
              <button type="button" onClick={onContinueToCause} className="ml-2 text-[12px] font-medium text-slate-600 hover:text-slate-900">Continuar para a análise</button>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
};

export function analysisMethodLabel(method: CauseAnalysisMethod | undefined): string {
  if (method === 'five_whys') return METHOD_LABEL.five_whys;
  if (method === 'ishikawa') return METHOD_LABEL.ishikawa;
  if (method === 'free') return METHOD_LABEL.simple;
  return 'Não registrado';
}
